import { businessInfo } from '../data/business';
import { Phone, MapPin, ArrowRight, Zap } from 'lucide-react';
import { guaranteeLogo } from '../data/galleryImages';

export default function Hero() {
  return (
    <section id="home" className="relative min-h-[100dvh] flex items-center pt-24 pb-12 overflow-hidden bg-white">
      {/* Background Pattern - Subtle Industrial/Tech */}
      <div className="absolute inset-0 z-0 opacity-10 pointer-events-none">
        <div className="absolute right-0 top-0 w-1/2 h-full bg-[radial-gradient(#DC2626_1px,transparent_1px)] [background-size:20px_20px]"></div>
        <div className="absolute left-0 bottom-0 w-1/3 h-1/2 bg-[linear-gradient(45deg,#DC2626_1px,transparent_1px)] [background-size:30px_30px] opacity-20"></div>
      </div>

      {/* Red Accent Lines */}
      <div className="absolute top-0 right-0 w-2 h-1/3 bg-primary hidden lg:block"></div>
      <div className="absolute bottom-0 left-0 w-full h-2 bg-primary"></div>
      <div className="absolute top-24 right-10 w-20 h-20 border-4 border-primary/10 rounded-full hidden lg:block"></div>
      <div className="absolute bottom-20 left-10 w-32 h-32 border-2 border-primary/5 rounded-full hidden lg:block"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div className="max-w-3xl pt-4 lg:pt-0 text-center lg:text-left">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-sm mb-6 border-l-4 border-primary shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700 mx-auto lg:mx-0">
            <Zap size={16} className="text-primary fill-primary" />
            <span className="text-xs font-bold text-text uppercase tracking-wider">
              Lider u industrijskoj automatici
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold text-text mb-6 leading-tight animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-100">
            Profesionalne <br className="hidden sm:block"/>
            <span className="text-primary relative inline-block">
              Elektro Usluge
              <svg className="absolute w-full h-2 sm:h-3 -bottom-1 left-0 text-primary opacity-30" viewBox="0 0 100 10" preserveAspectRatio="none">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
              </svg>
            </span> <br className="hidden sm:block"/>
            u Beogradu
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-gray-800 mb-8 max-w-xl leading-relaxed font-medium animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 mx-auto lg:mx-0">
            Specijalizovani za <strong className="text-primary font-bold">semiranje elektro ormara</strong>, instalacije jake i slabe struje. 
            Preciznost, sigurnost i poštovanje rokova.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 justify-center lg:justify-start">
            <a 
              href={`tel:${businessInfo.phoneClean}`}
              className="group relative overflow-hidden px-6 py-4 bg-primary text-white rounded-sm font-bold text-lg shadow-lg hover:bg-primary-dark transition-all active:scale-95 flex items-center justify-center gap-3 border-2 border-primary min-h-[56px]"
            >
              <Phone className="animate-pulse" strokeWidth={2.5} size={20} />
              <span>POZOVITE ODMAH</span>
            </a>
            
            <a 
              href={businessInfo.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-4 bg-white text-text border-2 border-gray-200 rounded-sm font-bold text-lg hover:border-primary hover:text-primary transition-all active:scale-95 flex items-center justify-center gap-3 shadow-sm min-h-[56px]"
            >
              <MapPin strokeWidth={2.5} size={20} />
              <span>Navigacija do nas</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform text-primary" />
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="mt-10 sm:mt-16 flex flex-wrap justify-center lg:justify-start items-center gap-x-6 gap-y-3 text-sm sm:text-base font-bold text-gray-700 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-primary rotate-45"></div>
              20+ Godina iskustva
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-primary rotate-45"></div>
              Licencirani električari
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 bg-primary rotate-45"></div>
              Garancija na radove
            </div>
          </div>
        </div>

        {/* Hero Image - Clean, No Overlay */}
        <div className="relative hidden lg:block h-full min-h-[500px] animate-in fade-in slide-in-from-right-8 duration-1000 delay-300">
          <div className="absolute top-8 right-8 w-full h-full border-4 border-primary/10 rounded-lg z-0"></div>
          <div className="absolute top-0 right-0 w-full h-full bg-gray-100 rounded-lg overflow-hidden shadow-2xl z-10 border-b-8 border-primary">
            <img 
              src="https://public.youware.com/users-website-assets/prod/2b367587-8bc6-4eab-be8e-3e6539f4d510/191e279636f44c8880b3c6fe74d5c7a3.jpg" 
              alt="Industrial Electrical Panel" 
              className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
            />
          </div>
          
          {/* Floating Card */}
          <div className="absolute bottom-8 -left-8 bg-white p-5 rounded shadow-xl border-l-4 border-primary z-20 max-w-xs">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center text-primary">
                <Zap size={20} fill="currentColor" />
              </div>
              <div>
                <p className="font-bold text-base text-text">Brza Intervencija</p>
                <p className="text-xs text-gray-600">Dostupni za hitne slučajeve</p>
              </div>
            </div>
            <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
              <div className="bg-primary h-full w-3/4"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Guarantee Logo - Between Hero and About */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-30 hidden sm:block">
        <div className="bg-white p-4 rounded-full shadow-2xl border-4 border-primary/10">
          <img 
            src={guaranteeLogo} 
            alt="100% Garancija Kvaliteta" 
            className="w-32 h-32 object-contain"
          />
        </div>
      </div>
    </section>
  );
}
